import { Col, Flex, Form, Row, Tooltip, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { type ProjectFormData } from "../../../project-form-data.ts"

const options: Intl.NumberFormatOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
}

const { Text } = Typography

interface MilestonesFooterRowProps {
  activeProjectTypeId: string | null | undefined
  form: FormInstance<ProjectFormData>
}

export function MilestonesFooterRow({
  activeProjectTypeId,
  form
}: MilestonesFooterRowProps) {
  const { t } = useTranslation()
  const watchedSalesPrice = Form.useWatch("sales_price", form)
  const phases = Form.useWatch("project_phases", form)
  const parsedSalesPrice = Number(watchedSalesPrice ?? 0)
  const salesPrice = Number.isFinite(parsedSalesPrice) ? parsedSalesPrice : 0

  const totals = useMemo(() => {
    const result = {
      currentTypeReceivable: 0,
      totalReceivable: 0,
      currentTypePayable: 0,
      totalPayable: 0
    }

    if (!phases?.length) return result

    for (const phase of phases) {
      const receivable = Number(phase?.planned_receivable || 0)
      const payable = Number(phase?.planned_payable || 0)

      result.totalReceivable += receivable
      result.totalPayable += payable

      if (phase?.project_type === activeProjectTypeId) {
        result.currentTypeReceivable += receivable
        result.currentTypePayable += payable
      }
    }

    return result
  }, [phases, activeProjectTypeId])

  const totalExceeded = totals?.totalReceivable > salesPrice
  const payableAtOrOverReceivable =
    totals.totalPayable > 0 && totals.totalPayable >= totals.totalReceivable

  return (
    <Flex
      vertical
      gap={4}
      className="border-b bg-primary/10 rounded-b-card border-border last:border-none px-2 py-1"
    >
      {/* Current Project Type Total */}
      <Row className="flex-nowrap flex-1" gutter={8}>
        <Col flex="auto" />
        <Col className="text-end" flex="120px">
          <Text type="secondary">
            {t("tabbedForm.milestones.totals.subtotal")}
          </Text>
        </Col>
        <Col flex="155px">
          <Text>{budgetFormat(totals?.currentTypeReceivable, options)}</Text>
        </Col>
        <Col flex="155px">
          <Text>{budgetFormat(totals?.currentTypePayable, options)}</Text>
        </Col>
        <Col flex="42px" />
      </Row>

      {/* Grand Total */}
      <Row className="flex-nowrap flex-1" gutter={8}>
        <Col flex="auto" />
        <Col className="text-end" flex="120px">
          <Text type={totalExceeded ? "warning" : undefined} strong>
            {t("tabbedForm.milestones.totals.total")}
          </Text>
        </Col>

        <Col flex="155px">
          <Tooltip
            title={t("tabbedForm.milestones.totals.receivableExceedsSalesPrice")}
            open={totalExceeded ? undefined : false}
          >
            <Text type={totalExceeded ? "warning" : undefined} strong>
              {budgetFormat(totals?.totalReceivable, options)}
            </Text>
          </Tooltip>
        </Col>
        <Col flex="155px">
          <Tooltip
            title={t(
              "tabbedForm.milestones.totals.payableAtOrOverReceivable"
            )}
            open={payableAtOrOverReceivable ? undefined : false}
          >
            <Text
              type={payableAtOrOverReceivable ? "warning" : undefined}
              strong
            >
              {budgetFormat(totals?.totalPayable, options)}
            </Text>
          </Tooltip>
        </Col>
        <Col flex="42px" />
      </Row>

      {/* Sales Price */}
      <Row className="flex-nowrap flex-1" gutter={8}>
        <Col flex="auto" />
        <Col className="text-end" flex="120px">
          <Text>{t("tabbedForm.milestones.totals.salesPrice")}</Text>
        </Col>
        <Col flex="155px">
          <Text>{budgetFormat(salesPrice, options)}</Text>
        </Col>
        <Col flex="155px" />
        <Col flex="42px" />
      </Row>
    </Flex>
  )
}
